import * as React from "react";
import * as moment from "moment";
import * as _ from "lodash";
import { Modal, Button, Form, FormGroup, Col, FormControl, ControlLabel, Alert } from "react-bootstrap";
import { ILogisticsProvider, ILogistics, ILogisticsRequest, ITransportType, IContact, ICenterToCenterDistance } from "@nkr/models/index";
import { ActiveLogisticsPlan, TransportMethod, LogisticsPlanAudience } from "@nkr/enums/index";
import { ILogisticsState } from "./logistics-state";
import { ILogisticsDates } from "./logistics-date-validation";
import LogisticsEditContent from "./edit-content";
import DateValidationErrorModal from "./date-validation-error-modal";

interface IProps {
    show: boolean,
    clusterId: number,
    data: ILogistics,
    logisticsProviders: ILogisticsProvider[],
    distanceBetweenCentersList: ICenterToCenterDistance[],
    transportTypes: ITransportType[],
    logisticsList: ILogistics[],
    forCourier: boolean,
    onSwitchToLogisticsPlan: (positionNumber: number) => void,
    onSave: (logistics: ILogisticsRequest) => void,
    onCancel: () => void,
    onAddressesExport: (donorCenterId: number, recipCenterId: number, clusterId: number) => void,
    onContactsExport: (clusterId: number) => void
}

interface IState {
    data: ILogisticsState,
    prevPrimary: ILogisticsDates,
    prevBackUp: ILogisticsDates,
    prevActual: ILogisticsDates,
    messages: string[],
    showDateError: boolean,
    dirty: boolean;
}

const initialState: IState = {
    data: {
        logisticsProvider: "",
        jobNumber: "",
        donorSurgeonContactId: "",
        recipientSurgeonContactId: "",

        donorCoordinator: '',
        recipientCoordinator: '',

        kidneyPickupDateTime: "",
        kidneyPickupDateTimeValid: true,
        kidneyPickupDateTimeBackup: "",
        kidneyPickupDateTimeBackupValid: true,
        kidneyPickupDateTimeActual: "",
        kidneyPickupDateTimeActualValid: true,
        kidneyPickupDateTimeTz: "",

        pickupGroundMiles: "",
        pickupGroundMilesBackup: "",
        pickupGroundMilesActual: "",

        flightRouteOrGround: "",
        flightRouteOrGroundBackup: "",
        flightRouteOrGroundActual: "",

        dropoffGroundMiles: "",
        dropoffGroundMilesBackup: "",
        dropoffGroundMilesActual: "",

        kidneyArrivalDateTime: "",
        kidneyArrivalDateTimeValid: true,
        kidneyArrivalDateTimeBackup: "",
        kidneyArrivalDateTimeBackupValid: true,
        kidneyArrivalDateTimeActual: "",
        kidneyArrivalDateTimeActualValid: true,
        kidneyArrivalDateTimeTz: "",

        transportMethod: "",
        transportMethodBackup: "",
        transportMethodActual: "",

        transportCost: "",
        transportCostBackup: "",
        transportCostActual: "",

        courierTransportCost: '',
        courierTransportCostBackup: '',

        flightCarrierAndNumber: "",
        flightCarrierAndNumberBackup: "",
        flightCarrierAndNumberActual: "",

        flightDepartureTime: "",
        flightDepartureTimeTz: "",
        flightDepartureTimeValid: true,
        flightDepartureTimeBackup: "",
        flightDepartureTimeBackupTz: "",
        flightDepartureTimeBackupValid: true,
        flightDepartureTimeActual: "",
        flightDepartureTimeActualTz: "",
        flightDepartureTimeActualValid: true,

        flightArrivalTime: "",
        flightArrivalTimeTz: "",
        flightArrivalTimeValid: true,
        flightArrivalTimeBackup: "",
        flightArrivalTimeBackupTz: "",
        flightArrivalTimeBackupValid: true,
        flightArrivalTimeActual: "",
        flightArrivalTimeActualTz: "",
        flightArrivalTimeActualValid: true,

        tenderedToAirlineActual: "",
        tenderedToAirlineActualTz: "",
        tenderedToAirlineActualValid: true,
        recoveredFromAirlineActual: "",
        recoveredFromAirlineActualTz: "",
        recoveredFromAirlineActualValid: true,

        leg2DepartureTime: "",
        leg2DepartureTimeTz: "",
        leg2DepartureTimeValid: true,
        leg2DepartureTimeBackup: "",
        leg2DepartureTimeBackupTz: "",
        leg2DepartureTimeBackupValid: true,
        leg2DepartureTimeActual: "",
        leg2DepartureTimeActualTz: "",
        leg2DepartureTimeActualValid: true,

        leg2ArrivalTime: "",
        leg2ArrivalTimeTz: "",
        leg2ArrivalTimeValid: true,
        leg2ArrivalTimeBackup: "",
        leg2ArrivalTimeBackupTz: "",
        leg2ArrivalTimeBackupValid: true,
        leg2ArrivalTimeActual: "",
        leg2ArrivalTimeActualTz: "",
        leg2ArrivalTimeActualValid: true,

        deliveryLocation: "",
        deliveredTo: "",

        activeLogisticsPlan: "",

        notes: ""
    },
    prevPrimary: {
        surgeryPickUpTime: '',
        surgeryPickUpTimeTz: '',
        departureTime: '',
        departureTimeTz: '',
        arrivalTime: '',
        arrivalTimeTz: '',
        leg2DepartureTime: '',
        leg2DepartureTimeTz: '',
        leg2ArrivalTime: '',
        leg2ArrivalTimeTz: '',
        kidneyArrivalTime: '',
        kidneyArrivalTimeTz: ''
    },
    prevBackUp: {
        surgeryPickUpTime: '',
        surgeryPickUpTimeTz: '',
        departureTime: '',
        departureTimeTz: '',
        arrivalTime: '',
        arrivalTimeTz: '',
        leg2DepartureTime: '',
        leg2DepartureTimeTz: '',
        leg2ArrivalTime: '',
        leg2ArrivalTimeTz: '',
        kidneyArrivalTime: '',
        kidneyArrivalTimeTz: ''
    },
    prevActual: {
        surgeryPickUpTime: '',
        surgeryPickUpTimeTz: '',
        departureTime: '',
        departureTimeTz: '',
        arrivalTime: '',
        arrivalTimeTz: '',
        leg2DepartureTime: '',
        leg2DepartureTimeTz: '',
        leg2ArrivalTime: '',
        leg2ArrivalTimeTz: '',
        kidneyArrivalTime: '',
        kidneyArrivalTimeTz: '',
        tenderedTime: '',
        tenderedTimeTz: '',
        recoveredTime: '',
        recoveredTimeTz: ''
    },
    showDateError: false,
    messages: [],
    dirty: false
};

class Logistics extends React.Component<IProps, IState> {
    readonly state: IState = initialState;

    componentWillReceiveProps = (nextProps: IProps) => {
        if (nextProps.show && this.props.show == false) {
            let {
                logisticsProvider, donorCoordinator, donorSurgeon, recipientCoordinator, recipientSurgeon, jobNumber,
                kidneyPickupDateTime, kidneyPickupDateTimeBackup, kidneyPickupDateTimeActual, kidneyPickupDateTimeTz,
                kidneyArrivalDateTime, kidneyArrivalDateTimeBackup, kidneyArrivalDateTimeActual, kidneyArrivalDateTimeTz,
                flightDepartureTime, flightDepartureTimeTz, flightDepartureTimeBackup, flightDepartureTimeBackupTz, flightDepartureTimeActual, flightDepartureTimeActualTz,
                flightArrivalTime, flightArrivalTimeTz, flightArrivalTimeBackup, flightArrivalTimeBackupTz, flightArrivalTimeActual, flightArrivalTimeActualTz,
                tenderedToAirlineActual, tenderedToAirlineActualTz,
                recoveredFromAirlineActual, recoveredFromAirlineActualTz,
                leg2DepartureTime, leg2DepartureTimeTz, leg2DepartureTimeBackup, leg2DepartureTimeBackupTz, leg2DepartureTimeActual, leg2DepartureTimeActualTz,
                leg2ArrivalTime, leg2ArrivalTimeTz, leg2ArrivalTimeBackup, leg2ArrivalTimeBackupTz, leg2ArrivalTimeActual, leg2ArrivalTimeActualTz,
                pickupGroundMiles, pickupGroundMilesBackup, pickupGroundMilesActual,
                flightRouteOrGround, flightRouteOrGroundBackup, flightRouteOrGroundActual,
                dropoffGroundMiles, dropoffGroundMilesBackup, dropoffGroundMilesActual,
                transportMethod, transportMethodBackup, transportMethodActual,
                transportCost, transportCostBackup, transportCostActual,
                courierTransportCost, courierTransportCostBackup,
                flightCarrierAndNumber, flightCarrierAndNumberBackup, flightCarrierAndNumberActual,
                deliveryLocation, deliveredTo, activeLogisticsPlan,
                donorCenterId, rdnCenterId, recipientCenterId, logisticsPlanNotes } = nextProps.data;

            let donorCenter = rdnCenterId > 0 ? rdnCenterId : donorCenterId;

            if (transportMethod == "" && donorCenter == recipientCenterId) {
                transportMethod = TransportMethod.Internal.toString();
                transportMethodBackup = TransportMethod.Internal.toString();
                transportMethodActual = TransportMethod.Internal.toString();
            }

            this.setState({
                data: {
                    ...this.state.data,
                    logisticsProvider: logisticsProvider != null ? logisticsProvider : "",
                    donorSurgeonContactId: donorSurgeon ? donorSurgeon.id.toString() : '0',
                    recipientSurgeonContactId: recipientSurgeon ? recipientSurgeon.id.toString() : '0',
                    jobNumber,

                    donorCoordinator: donorCoordinator != null ? donorCoordinator.fullName : '',
                    donorCoordinatorId: donorCoordinator != null ? donorCoordinator.id.toString() : '0',
                    recipientCoordinator: recipientCoordinator != null ? recipientCoordinator.fullName : '',
                    recipientCoordinatorId: recipientCoordinator != null ? recipientCoordinator.id.toString() : '0',

                    kidneyPickupDateTime, kidneyPickupDateTimeValid: true,
                    kidneyPickupDateTimeBackup, kidneyPickupDateTimeBackupValid: true,
                    kidneyPickupDateTimeActual, kidneyPickupDateTimeActualValid: true,
                    kidneyPickupDateTimeTz,

                    kidneyArrivalDateTime, kidneyArrivalDateTimeValid: true,
                    kidneyArrivalDateTimeBackup, kidneyArrivalDateTimeBackupValue: true,
                    kidneyArrivalDateTimeActual, kidneyArrivalDateTimeActualValid: true,
                    kidneyArrivalDateTimeTz,

                    pickupGroundMiles, pickupGroundMilesBackup, pickupGroundMilesActual,
                    flightRouteOrGround, flightRouteOrGroundBackup, flightRouteOrGroundActual,
                    dropoffGroundMiles, dropoffGroundMilesBackup, dropoffGroundMilesActual,
                    transportMethod, transportMethodBackup, transportMethodActual,
                    transportCost, transportCostBackup, transportCostActual,

                    courierTransportCost, courierTransportCostBackup,

                    flightCarrierAndNumber, flightCarrierAndNumberBackup, flightCarrierAndNumberActual,

                    flightDepartureTime, flightDepartureTimeTz, flightDepartureTimeValid: true,
                    flightDepartureTimeBackup, flightDepartureTimeBackupTz, flightDepartureTimeBackupValid: true,
                    flightDepartureTimeActual, flightDepartureTimeActualTz, flightDepartureTimeActualValid: true,

                    flightArrivalTime, flightArrivalTimeTz, flightArrivalTimeValid: true,
                    flightArrivalTimeBackup, flightArrivalTimeBackupTz, flightArrivalTimeBackupValid: true,
                    flightArrivalTimeActual, flightArrivalTimeActualTz, flightArrivalTimeActualValid: true,

                    tenderedToAirlineActual, tenderedToAirlineActualTz, tenderedToAirlineActualValid: true,
                    recoveredFromAirlineActual, recoveredFromAirlineActualTz, recoveredFromAirlineActualValid: true,

                    leg2DepartureTime, leg2DepartureTimeTz, leg2DepartureTimeValid: true,
                    leg2DepartureTimeBackup, leg2DepartureTimeBackupTz, leg2DepartureTimeBackupValid: true,
                    leg2DepartureTimeActual, leg2DepartureTimeActualTz, leg2DepartureTimeActualValid: true,

                    leg2ArrivalTime, leg2ArrivalTimeTz, leg2ArrivalTimeValid: true,
                    leg2ArrivalTimeBackup, leg2ArrivalTimeBackupTz, leg2ArrivalTimeBackupValid: true,
                    leg2ArrivalTimeActual, leg2ArrivalTimeActualTz, leg2ArrivalTimeActualValid: true,

                    deliveryLocation, deliveredTo, activeLogisticsPlan,
                    notes: logisticsPlanNotes
                },
                prevPrimary: {
                    surgeryPickUpTime: kidneyPickupDateTime,
                    surgeryPickUpTimeTz: kidneyPickupDateTimeTz,
                    departureTime: flightDepartureTime,
                    departureTimeTz: flightDepartureTimeTz,
                    arrivalTime: flightArrivalTime,
                    arrivalTimeTz: flightArrivalTimeTz,
                    leg2DepartureTime: leg2DepartureTime,
                    leg2DepartureTimeTz: leg2DepartureTimeTz,
                    leg2ArrivalTime: leg2ArrivalTime,
                    leg2ArrivalTimeTz: leg2ArrivalTimeTz,
                    kidneyArrivalTime: kidneyArrivalDateTime,
                    kidneyArrivalTimeTz: kidneyArrivalDateTimeTz,
                },
                prevBackUp: {
                    surgeryPickUpTime: kidneyPickupDateTimeBackup,
                    surgeryPickUpTimeTz: kidneyPickupDateTimeTz,
                    departureTime: flightDepartureTimeBackup,
                    departureTimeTz: flightDepartureTimeBackupTz,
                    arrivalTime: flightArrivalTimeBackup,
                    arrivalTimeTz: flightArrivalTimeBackupTz,
                    leg2DepartureTime: leg2DepartureTimeBackup,
                    leg2DepartureTimeTz: leg2DepartureTimeBackupTz,
                    leg2ArrivalTime: leg2ArrivalTimeBackup,
                    leg2ArrivalTimeTz: leg2ArrivalTimeBackupTz,
                    kidneyArrivalTime: kidneyArrivalDateTimeBackup,
                    kidneyArrivalTimeTz: kidneyArrivalDateTimeTz,
                },
                prevActual: {
                    surgeryPickUpTime: kidneyPickupDateTimeActual,
                    surgeryPickUpTimeTz: kidneyPickupDateTimeTz,
                    departureTime: flightDepartureTimeActual,
                    departureTimeTz: flightDepartureTimeActualTz,
                    arrivalTime: flightArrivalTimeActual,
                    arrivalTimeTz: flightArrivalTimeActualTz,
                    leg2DepartureTime: leg2DepartureTimeActual,
                    leg2DepartureTimeTz: leg2DepartureTimeActualTz,
                    leg2ArrivalTime: leg2ArrivalTimeActual,
                    leg2ArrivalTimeTz: leg2ArrivalTimeActualTz,
                    kidneyArrivalTime: kidneyArrivalDateTimeActual,
                    kidneyArrivalTimeTz: kidneyArrivalDateTimeTz,
                    tenderedTime: tenderedToAirlineActual,
                    tenderedTimeTz: tenderedToAirlineActualTz,
                    recoveredTime: recoveredFromAirlineActual,
                    recoveredTimeTz: recoveredFromAirlineActualTz
                },
                dirty: false,
                messages: [],
                showDateError: false
            });
        }
    }

    componentWillUnmount = () => {
        this.setState({
            messages: [],
            showDateError: false
        })
    }

    handleDateTimeFieldChange = (field: string, e: string) => {
        let data = { ...this.state.data };
        data[field] = e;

        this.setState({
            data,
            dirty: true
        });
    }

    handleFieldChange = (field: string, value: string) => {
        let data = { ...this.state.data };
        data[field] = value;

        this.setState({
            data,
            dirty: true
        });
    }

    handleDropdownChange = (id: string, value: string) => {
        let data = { ...this.state.data };
        data[id] = value;

        this.setState({
            data,
            dirty: true
        });
    }

    handleActiveLogisticsPlanChange = (activeLogisticsPlan: ActiveLogisticsPlan) => {
        let data = { ...this.state.data, activeLogisticsPlan: activeLogisticsPlan.toString() };

        this.setState({
            data,
            dirty: true
        });
    }

    handleAddressDownload = () => {
        let { donorCenterId, recipientCenterId } = this.props.data;
        let clusterId = this.props.clusterId;

        this.props.onAddressesExport(donorCenterId, recipientCenterId, clusterId);
    }


    handleContactsDownload = () => {
        let { donorCenterId, recipientCenterId } = this.props.data;
        let clusterId = this.props.clusterId;

        this.props.onContactsExport(clusterId);
    }


    handleCopyPrimary = () => {
        var data = { ...this.state.data };

        data.kidneyPickupDateTimeActual = data.kidneyPickupDateTime;
        data.pickupGroundMilesActual = data.pickupGroundMiles;
        data.flightRouteOrGroundActual = data.flightRouteOrGround;
        data.transportMethodActual = data.transportMethod;
        data.transportCostActual = data.transportCost;
        data.flightCarrierAndNumberActual = data.flightCarrierAndNumber;
        data.flightDepartureTimeActual = data.flightDepartureTime;
        data.flightDepartureTimeActualTz = data.flightDepartureTimeTz;
        data.flightArrivalTimeActual = data.flightArrivalTime;
        data.flightArrivalTimeActualTz = data.flightArrivalTimeTz;
        data.leg2DepartureTimeActual = data.leg2DepartureTime;
        data.leg2DepartureTimeActualTz = data.leg2DepartureTimeTz;
        data.leg2ArrivalTimeActual = data.leg2ArrivalTime;
        data.leg2ArrivalTimeActualTz = data.leg2ArrivalTimeTz;
        data.kidneyArrivalDateTimeActual = data.kidneyArrivalDateTime;

        this.setState({
            data,
            dirty: true
        });
    }

    handleCopyBackup = () => {
        var data = { ...this.state.data };

        data.kidneyPickupDateTimeActual = data.kidneyPickupDateTimeBackup;
        data.pickupGroundMilesActual = data.pickupGroundMilesBackup;
        data.flightRouteOrGroundActual = data.flightRouteOrGroundBackup;
        data.transportMethodActual = data.transportMethodBackup;
        data.transportCostActual = data.transportCostBackup;
        data.flightCarrierAndNumberActual = data.flightCarrierAndNumberBackup;
        data.flightDepartureTimeActual = data.flightDepartureTimeBackup;
        data.flightDepartureTimeActualTz = data.flightDepartureTimeBackupTz;
        data.flightArrivalTimeActual = data.flightArrivalTimeBackup;
        data.flightArrivalTimeActualTz = data.flightArrivalTimeBackupTz;
        data.leg2DepartureTimeActual = data.leg2DepartureTimeBackup;
        data.leg2DepartureTimeActualTz = data.leg2DepartureTimeBackupTz;
        data.leg2ArrivalTimeActual = data.leg2ArrivalTimeBackup;
        data.leg2ArrivalTimeActualTz = data.leg2ArrivalTimeBackupTz;
        data.kidneyArrivalDateTimeActual = data.kidneyArrivalDateTimeBackup;

        this.setState({
            data,
            dirty: true
        });
    }

    handleDownload = (e: React.MouseEvent<Button, MouseEvent>) => {
        /*let item = e.currentTarget['value'];
        window.open(__OUTSIDEURL__ + '/manage/download/' + item + '?cluster=' + this.props.clusterId);


let { donorCenterId, recipientCenterId } = this.props.data;*/
        let clusterId = this.props.clusterId;

        this.props.onContactsExport(clusterId);

    }

    handleSave = () => {
        let {
            kidneyPickupDateTime, kidneyPickupDateTimeTz, kidneyArrivalDateTimeTz, kidneyPickupDateTimeBackup, kidneyPickupDateTimeActual,
            pickupGroundMiles, pickupGroundMilesBackup, pickupGroundMilesActual,
            dropoffGroundMiles, dropoffGroundMilesBackup, dropoffGroundMilesActual,
            kidneyArrivalDateTime, kidneyArrivalDateTimeBackup, kidneyArrivalDateTimeActual,
            flightRouteOrGround, flightRouteOrGroundBackup, flightRouteOrGroundActual,
            transportMethod, transportMethodBackup, transportMethodActual,
            transportCost, transportCostBackup, transportCostActual,
            flightCarrierAndNumber, flightCarrierAndNumberBackup, flightCarrierAndNumberActual,
            flightDepartureTime, flightDepartureTimeTz, flightDepartureTimeBackup, flightDepartureTimeBackupTz, flightDepartureTimeActual, flightDepartureTimeActualTz,
            flightArrivalTime, flightArrivalTimeTz, flightArrivalTimeBackup, flightArrivalTimeBackupTz, flightArrivalTimeActual, flightArrivalTimeActualTz,
            leg2DepartureTime, leg2DepartureTimeTz, leg2DepartureTimeBackup, leg2DepartureTimeBackupTz, leg2DepartureTimeActual, leg2DepartureTimeActualTz,
            leg2ArrivalTime, leg2ArrivalTimeTz, leg2ArrivalTimeBackup, leg2ArrivalTimeBackupTz, leg2ArrivalTimeActual, leg2ArrivalTimeActualTz,
            tenderedToAirlineActual, tenderedToAirlineActualTz, recoveredFromAirlineActual, recoveredFromAirlineActualTz,
            deliveryLocation, deliveredTo, activeLogisticsPlan,
            logisticsProvider,
            jobNumber,
            donorSurgeonContactId,
            donorCoordinatorId,
            recipientSurgeonContactId,
            recipientCoordinatorId,
            notes
        } = this.state.data;

        let messages = [];

        let primaryDateValidation = this.handleDateValidation({
            surgeryPickUpTime: kidneyPickupDateTime,
            surgeryPickUpTimeTz: kidneyPickupDateTimeTz,
            departureTime: flightDepartureTime,
            departureTimeTz: flightDepartureTimeTz,
            arrivalTime: flightArrivalTime,
            arrivalTimeTz: flightArrivalTimeTz,
            leg2DepartureTime: leg2DepartureTime,
            leg2DepartureTimeTz: leg2DepartureTimeTz,
            leg2ArrivalTime: leg2ArrivalTime,
            leg2ArrivalTimeTz: leg2ArrivalTimeTz,
            kidneyArrivalTime: kidneyArrivalDateTime,
            kidneyArrivalTimeTz: kidneyArrivalDateTimeTz
        }, this.state.prevPrimary, 'Primary', transportMethod);

        if (primaryDateValidation != '') {
            messages.push(primaryDateValidation)
        }

        let backUpDateValidation = this.handleDateValidation({
            surgeryPickUpTime: kidneyPickupDateTimeBackup,
            surgeryPickUpTimeTz: kidneyPickupDateTimeTz,
            departureTime: flightDepartureTimeBackup,
            departureTimeTz: flightDepartureTimeBackupTz,
            arrivalTime: flightArrivalTimeBackup,
            arrivalTimeTz: flightArrivalTimeBackupTz,
            leg2DepartureTime: leg2DepartureTimeBackup,
            leg2DepartureTimeTz: leg2DepartureTimeBackupTz,
            leg2ArrivalTime: leg2ArrivalTimeBackup,
            leg2ArrivalTimeTz: leg2ArrivalTimeBackupTz,
            kidneyArrivalTime: kidneyArrivalDateTimeBackup,
            kidneyArrivalTimeTz: kidneyArrivalDateTimeTz
        }, this.state.prevBackUp, 'Backup', transportMethodBackup);

        if (backUpDateValidation != '') {
            messages.push(backUpDateValidation)
        }

        let actualDateValidation = this.handleDateValidation({
            surgeryPickUpTime: kidneyPickupDateTimeActual,
            surgeryPickUpTimeTz: kidneyPickupDateTimeTz,
            departureTime: flightDepartureTimeActual,
            departureTimeTz: flightDepartureTimeActualTz,
            arrivalTime: flightArrivalTimeActual,
            arrivalTimeTz: flightArrivalTimeActualTz,
            leg2DepartureTime: leg2DepartureTimeActual,
            leg2DepartureTimeTz: leg2DepartureTimeActualTz,
            leg2ArrivalTime: leg2ArrivalTimeActual,
            leg2ArrivalTimeTz: leg2ArrivalTimeActualTz,
            kidneyArrivalTime: kidneyArrivalDateTimeActual,
            kidneyArrivalTimeTz: kidneyArrivalDateTimeTz,
            tenderedTime: tenderedToAirlineActual,
            tenderedTimeTz: tenderedToAirlineActualTz,
            recoveredTime: recoveredFromAirlineActual,
            recoveredTimeTz: recoveredFromAirlineActualTz
        }, this.state.prevActual, 'Actual', transportMethodActual);

        if (actualDateValidation != '') {
            messages.push(actualDateValidation)
        }

        if (messages.length > 0) {
            this.setState({
                messages,
                showDateError: true,
            })

            return null;
        } else {
            this.setState({
                messages: [],
                showDateError: false,
            })
        }

        let kidneyPickupDateTimeValid = kidneyPickupDateTime ? moment(kidneyPickupDateTime, 'MM/DD/YY HH:mm', true).isValid() : true;
        let kidneyPickupDateTimeBackupValid = kidneyPickupDateTimeBackup ? moment(kidneyPickupDateTimeBackup, 'MM/DD/YY HH:mm', true).isValid() : true;
        let kidneyPickupDateTimeActualValid = kidneyPickupDateTimeActual ? moment(kidneyPickupDateTimeActual, 'MM/DD/YY HH:mm', true).isValid() : true;

        let kidneyArrivalDateTimeValid = kidneyArrivalDateTime ? moment(kidneyArrivalDateTime, 'MM/DD/YY HH:mm', true).isValid() : true;
        let kidneyArrivalDateTimeBackupValid = kidneyArrivalDateTimeBackup ? moment(kidneyArrivalDateTimeBackup, 'MM/DD/YY HH:mm', true).isValid() : true;
        let kidneyArrivalDateTimeActualValid = kidneyArrivalDateTimeActual ? moment(kidneyArrivalDateTimeActual, 'MM/DD/YY HH:mm', true).isValid() : true;

        let flightDepartureTimeValid = flightDepartureTime ? moment(flightDepartureTime, 'MM/DD/YY HH:mm', true).isValid() : true;
        let flightDepartureTimeBackupValid = flightDepartureTimeBackup ? moment(flightDepartureTimeBackup, 'MM/DD/YY HH:mm', true).isValid() : true;
        let flightDepartureTimeActualValid = flightDepartureTimeActual ? moment(flightDepartureTimeActual, 'MM/DD/YY HH:mm', true).isValid() : true;

        let flightArrivalTimeValid = flightArrivalTime ? moment(flightArrivalTime, 'MM/DD/YY HH:mm', true).isValid() : true;
        let flightArrivalTimeBackupValid = flightArrivalTimeBackup ? moment(flightArrivalTimeBackup, 'MM/DD/YY HH:mm', true).isValid() : true;
        let flightArrivalTimeActualValid = flightArrivalTimeActual ? moment(flightArrivalTimeActual, 'MM/DD/YY HH:mm', true).isValid() : true;

        let leg2DepartureTimeValid = leg2DepartureTime ? moment(leg2DepartureTime, 'MM/DD/YY HH:mm', true).isValid() : true;
        let leg2DepartureTimeBackupValid = leg2DepartureTimeBackup ? moment(leg2DepartureTimeBackup, 'MM/DD/YY HH:mm', true).isValid() : true;
        let leg2DepartureTimeActualValid = leg2DepartureTimeActual ? moment(leg2DepartureTimeActual, 'MM/DD/YY HH:mm', true).isValid() : true;

        let leg2ArrivalTimeValid = leg2ArrivalTime ? moment(leg2ArrivalTime, 'MM/DD/YY HH:mm', true).isValid() : true;
        let leg2ArrivalTimeBackupValid = leg2ArrivalTimeBackup ? moment(leg2ArrivalTimeBackup, 'MM/DD/YY HH:mm', true).isValid() : true;
        let leg2ArrivalTimeActualValid = leg2ArrivalTimeActual ? moment(leg2ArrivalTimeActual, 'MM/DD/YY HH:mm', true).isValid() : true;

        let tenderedToAirlineActualValid = tenderedToAirlineActual ? moment(tenderedToAirlineActual, 'MM/DD/YY HH:mm', true).isValid() : true;
        let recoveredFromAirlineActualValid = recoveredFromAirlineActual ? moment(recoveredFromAirlineActual, 'MM/DD/YY HH:mm', true).isValid() : true;

        let valid =
            kidneyPickupDateTimeValid && kidneyPickupDateTimeBackupValid && kidneyPickupDateTimeActualValid
            && kidneyArrivalDateTimeValid && kidneyArrivalDateTimeBackupValid && kidneyArrivalDateTimeActualValid
            && flightDepartureTimeValid && flightDepartureTimeBackupValid && flightDepartureTimeActualValid
            && flightArrivalTimeValid && flightArrivalTimeBackupValid && flightArrivalTimeActualValid
            && leg2DepartureTimeValid && leg2DepartureTimeBackupValid && leg2DepartureTimeActualValid
            && leg2ArrivalTimeValid && leg2ArrivalTimeBackupValid && leg2ArrivalTimeActualValid
            && tenderedToAirlineActualValid && recoveredFromAirlineActualValid;

        var data: ILogisticsRequest = {
            recipientId: this.props.data.recipientId,
            recipientSurgeonContactId: recipientSurgeonContactId != '' ? parseInt(recipientSurgeonContactId) : 0,
            donorId: this.props.data.donorId,
            donorSurgeonContactId: parseInt(donorSurgeonContactId),
            logisticsProvider: logisticsProvider != '' ? parseInt(logisticsProvider) : null,
            kidneyPickupDateTime: kidneyPickupDateTime != '' ? moment(kidneyPickupDateTime).format('MM/DD/YY HH:mm') : null,
            kidneyPickupDateTimeBackup: kidneyPickupDateTimeBackup != '' ? moment(kidneyPickupDateTimeBackup).format('MM/DD/YY HH:mm') : null,
            kidneyPickupDateTimeActual: kidneyPickupDateTimeActual != '' ? moment(kidneyPickupDateTimeActual).format('MM/DD/YY HH:mm') : null,
            pickupGroundMiles: pickupGroundMiles != '' ? parseInt(pickupGroundMiles) : null,
            pickupGroundMilesBackup: pickupGroundMilesBackup != '' ? parseInt(pickupGroundMiles) : null,
            pickupGroundMilesActual: pickupGroundMilesActual != '' ? parseInt(pickupGroundMilesActual) : null,
            dropoffGroundMiles: dropoffGroundMiles != '' ? parseInt(dropoffGroundMiles) : null,
            dropoffGroundMilesBackup: dropoffGroundMilesBackup != '' ? parseInt(dropoffGroundMilesBackup) : null,
            dropoffGroundMilesActual: dropoffGroundMilesActual != '' ? parseInt(dropoffGroundMilesActual) : null,
            kidneyArrivalDateTime: kidneyArrivalDateTime != '' ? moment(kidneyArrivalDateTime).format('MM/DD/YY HH:mm') : null,
            kidneyArrivalDateTimeBackup: kidneyArrivalDateTimeBackup != '' ? moment(kidneyArrivalDateTimeBackup).format('MM/DD/YY HH:mm') : null,
            kidneyArrivalDateTimeActual: kidneyArrivalDateTimeActual != '' ? moment(kidneyArrivalDateTimeActual).format('MM/DD/YY HH:mm') : null,
            flightRouteOrGround,
            flightRouteOrGroundBackup,
            flightRouteOrGroundActual,
            transportMethod: transportMethod != '' ? parseInt(transportMethod) : null,
            transportMethodBackup: transportMethodBackup != '' ? parseInt(transportMethodBackup) : null,
            transportMethodActual: transportMethodActual != '' ? parseInt(transportMethodActual) : null,
            transportCost: transportCost != '' ? parseInt(transportCost) : null,
            transportCostBackup: transportCostBackup != '' ? parseInt(transportCostBackup) : null,
            transportCostActual: transportCostActual != '' ? parseInt(transportCostActual) : null,
            flightCarrierAndNumber,
            flightCarrierAndNumberBackup,
            flightCarrierAndNumberActual,
            flightDepartureTime: flightDepartureTime != '' ? moment(flightDepartureTime).format('MM/DD/YY HH:mm') : null,
            flightDepartureTimeTz,
            flightDepartureTimeBackup: flightDepartureTimeBackup != '' ? moment(flightDepartureTimeBackup).format('MM/DD/YY HH:mm') : null,
            flightDepartureTimeBackupTz,
            flightDepartureTimeActual: flightDepartureTimeActual != '' ? moment(flightDepartureTimeActual).format('MM/DD/YY HH:mm') : null,
            flightDepartureTimeActualTz,
            flightArrivalTime: flightArrivalTime != '' ? moment(flightArrivalTime).format('MM/DD/YY HH:mm') : null,
            flightArrivalTimeTz,
            flightArrivalTimeBackup: flightArrivalTimeBackup != '' ? moment(flightArrivalTimeBackup).format('MM/DD/YY HH:mm') : null,
            flightArrivalTimeBackupTz,
            flightArrivalTimeActual: flightArrivalTimeActual != '' ? moment(flightArrivalTimeActual).format('MM/DD/YY HH:mm') : null,
            flightArrivalTimeActualTz,
            leg2DepartureTime: leg2DepartureTime != '' ? moment(leg2DepartureTime).format('MM/DD/YY HH:mm') : null,
            leg2DepartureTimeTz,
            leg2DepartureTimeBackup: leg2DepartureTimeBackup != '' ? moment(leg2DepartureTimeBackup).format('MM/DD/YY HH:mm') : null,
            leg2DepartureTimeBackupTz,
            leg2DepartureTimeActual: leg2DepartureTimeActual != '' ? moment(leg2DepartureTimeActual).format('MM/DD/YY HH:mm') : null,
            leg2DepartureTimeActualTz,
            leg2ArrivalTime: leg2ArrivalTime != '' ? moment(leg2ArrivalTime).format('MM/DD/YY HH:mm') : null,
            leg2ArrivalTimeTz,
            leg2ArrivalTimeBackup: leg2ArrivalTimeBackup != '' ? moment(leg2ArrivalTimeBackup).format('MM/DD/YY HH:mm') : null,
            leg2ArrivalTimeBackupTz,
            leg2ArrivalTimeActual: leg2ArrivalTimeActual != '' ? moment(leg2ArrivalTimeActual).format('MM/DD/YY HH:mm') : null,
            leg2ArrivalTimeActualTz,
            tenderedToAirlineActual: tenderedToAirlineActual != '' ? moment(tenderedToAirlineActual).format('MM/DD/YY HH:mm') : null,
            tenderedToAirlineActualTz,
            recoveredFromAirlineActual: recoveredFromAirlineActual != '' ? moment(recoveredFromAirlineActual).format('MM/DD/YY HH:mm') : null,
            recoveredFromAirlineActualTz,
            deliveryLocation,
            deliveredTo,
            activeLogisticsPlan,
            chainId: this.props.data.chainId,
            clusterNumber: this.props.data.clusterNumber,
            position: parseInt(this.props.data.position),
            jobNumber,
            notes,
            audience: this.props.forCourier ? LogisticsPlanAudience.CourierVendor : LogisticsPlanAudience.Nkr
        };

        if (valid) {
            this.props.onSave(data);
        } else {
            this.setState({
                data: {
                    ...this.state.data,
                    kidneyPickupDateTimeValid,
                    kidneyPickupDateTimeBackupValid,
                    kidneyPickupDateTimeActualValid,

                    kidneyArrivalDateTimeValid,
                    kidneyArrivalDateTimeBackupValid,
                    kidneyArrivalDateTimeActualValid,

                    flightDepartureTimeValid,
                    flightDepartureTimeBackupValid,
                    flightDepartureTimeActualValid,

                    flightArrivalTimeValid,
                    flightArrivalTimeBackupValid,
                    flightArrivalTimeActualValid,

                    leg2DepartureTimeValid,
                    leg2DepartureTimeBackupValid,
                    leg2DepartureTimeActualValid,

                    leg2ArrivalTimeValid,
                    leg2ArrivalTimeBackupValid,
                    leg2ArrivalTimeActualValid,

                    tenderedToAirlineActualValid,
                    recoveredFromAirlineActualValid
                }
            });
        }
    }

    handleCancel = () => {
        this.props.onCancel();
    }

    handleSwitchToLogisticsPlan = () => {
        let { position } = this.props.data;
        this.props.onSwitchToLogisticsPlan(parseInt(position));
    }

    handleNotesChange = (e: React.FormEvent<FormControl & HTMLTextAreaElement>) => {
        let data = _.cloneDeep({ ...this.state.data, notes: e.currentTarget.value });

        this.setState({
            data,
            dirty: true
        });
    }

    handleDateValidation = (values: ILogisticsDates, prevValues: ILogisticsDates, type: string, transport: string) => {
        //Removed this code as of 6/26/24, we want the validation to always be thrown if there are dates in there. 
        //If theres no change to values, don't check
        // if (_.isEqual(values, prevValues)) {
        //     return '';
        // }

        let [surgeryPickUpTimeValid, surgeryPickUpTime] = this.handleDateConversion(values.surgeryPickUpTime, values.surgeryPickUpTimeTz);
        let [departureTimeValid, departureTime] = this.handleDateConversion(values.departureTime, values.departureTimeTz);
        let [arrivalTimeValid, arrivalTime] = this.handleDateConversion(values.arrivalTime, values.arrivalTimeTz);
        let [leg2DepartureTimeValid, leg2DepartureTime] = this.handleDateConversion(values.leg2DepartureTime, values.leg2DepartureTimeTz);
        let [leg2ArrivalTimeValid, leg2ArrivalTime] = this.handleDateConversion(values.leg2ArrivalTime, values.leg2ArrivalTimeTz);
        let [kidneyArrivalTimeValid, kidneyArrivalTime] = this.handleDateConversion(values.kidneyArrivalTime, values.kidneyArrivalTimeTz);

        let now = moment.utc();

        //If they're all blank don't worry
        if (!surgeryPickUpTimeValid
            && !kidneyArrivalTimeValid
            && !departureTimeValid
            && !arrivalTimeValid
            && !leg2DepartureTimeValid
            && !leg2ArrivalTimeValid) {
            return '';
        }

        let tenderedTime = moment();
        let tenderedTimeValid = false;
        let recoveredTime = moment();
        let recoveredTimeValid = false;

        const transportMethod = this.props.transportTypes.find(tt => tt.id == (transport != "" ? parseInt(transport) : 0));

        const singleLegs: string[] = ["Commercial", "Charter", "Ground", "Internal"];

        const disableLeg2 = transportMethod != null ? singleLegs.includes(transportMethod.description) : false;

        if (type == 'Actual') {
            [tenderedTimeValid, tenderedTime] = this.handleDateConversion(values.tenderedTime, values.tenderedTimeTz);
            [recoveredTimeValid, recoveredTime] = this.handleDateConversion(values.recoveredTime, values.recoveredTimeTz);
        }

        //Take min of Primary and Back Up to check if each date are within a 55 hour range
        let [minimumDate, minimumDateType] = this.returnPickUpMinimumDate();

        //When theres no Donor Surgery or Kidney Arrival
        // if (!surgeryPickUpTimeValid || !kidneyArrivalTimeValid) {
        //     return `${type} Donor Surgery/Pickup Date/Time and ${type} Kidney Arrival Date/Time must both be defined.`;
        // }

        // if (type != 'Actual') {
        //     //Donor Surgery/Pick up has to be in the future
        //     if (surgeryPickUpTime.isBefore(now)){
        //         return `${type} Donor Surgery/Pickup Date/Time has to be in the future. Please be cognizant of TimeZone.`;
        //     }
        // } else {
        //     //Donor Surgery/Pick up has to be in the future
        //     if (surgeryPickUpTime.isAfter(now)){
        //         return `${type} Donor Surgery/Pickup Date/Time has to be now or in the past. Please be cognizant of TimeZone.`;
        //     }

        //     //Donor Surgery/Pick up has to be in the future
        //     if (kidneyArrivalTime.isAfter(now)){
        //         return `${type} Kidney Arrival Date/Time has to be now or in the past. Please be cognizant of TimeZone.`;
        //     }
        // }

        let surgeryPlus48 = moment(surgeryPickUpTime).clone();
        surgeryPlus48.add(48, 'hours');

        // //Donor Surgery/Pickup Date/Time must be before Kidney Arrival Date/Time
        // if (surgeryPickUpTime.isAfter(kidneyArrivalTime)){
        //     return `${type} Donor Surgery/Pickup Date/Time must be before ${type} Kidney Arrival Date/Time. Please be cognizant of TimeZone.`;
        // }

        // //Donor Surgery/Pickup Date/Time and Kidney Arrival Date/Time must be in 48 hour range
        // if (surgeryPlus48.isBefore(kidneyArrivalTime)){
        //     return `${type} Donor Surgery/Pickup Date/Time and ${type} Kidney Arrival Date/Time must be in 48 hour range. Please be cognizant of TimeZone.`;
        // }

        if (surgeryPickUpTimeValid) {
            //check minimum against Donor Surgery/Pickup Date/Time
            if (Math.abs(surgeryPickUpTime.diff(minimumDate, 'hours')) > 55) {
                return `${type} Donor Surgery/Pickup Date/Time has to be within 55 hours of the ${minimumDateType} Donor Surgery/Pickup Date/Time`;
            }
        }

        if (kidneyArrivalTimeValid) {
            //check minimum against Arrival Date
            if (Math.abs(kidneyArrivalTime.diff(minimumDate, 'hours')) > 55) {
                return `${type} Kidney Arrival Date/Time has to be within 55 hours of the ${minimumDateType} Donor Surgery/Pickup Date/Time`;
            }
        }

        if (departureTimeValid) {
            // //Departure Date/Time TimeZone must be selected
            if (values.departureTimeTz == '') {
                return `${type} Departure Date/Time TimeZone must be selected`;
            }

            // //Departure Date/Time must be between Donor Surgery and Kidney Arrival
            // if (!departureTime.isBetween(surgeryPickUpTime, kidneyArrivalTime)) {
            //     return `${type} Departure Date/Time must be between ${type} Donor Surgery/Pickup Date/Time and ${type} Kidney Arrival Date/Time. Please be cognizant of TimeZone`;
            // }

            //check minimum against leg 1 departure
            if (Math.abs(departureTime.diff(minimumDate, 'hours')) > 55) {
                return `${type} Departure Date/Time has to be within 55 hours of the ${minimumDateType} Donor Surgery/Pickup Date/Time`;
            }
        }

        //Departure Date/Time must be valid for Arrival to be validated 
        // if (!departureTimeValid && arrivalTimeValid) {
        //     return `${type} Departure Date/Time must be populated for ${type} Arrival Date/Time to be validated`;
        // }


        if (arrivalTimeValid) {
            //TimeZone must be selected
            if (values.arrivalTimeTz == '') {
                return `${type} Arrival Date/Time TimeZone must be selected`;
            }

            // //Arrival must be after Departure and between Donor Surgery and Kidney Arrival 
            // if (arrivalTime.isBefore(departureTime) || !arrivalTime.isBetween(surgeryPickUpTime, kidneyArrivalTime)) {
            //     return `${type} Arrival Date/Time must be after ${type} Departure Date/Time, as well as between ${type} Donor Surgery/Pickup Date/Time and ${type} Kidney Arrival Date/Time. Please be cognizant of TimeZone`;
            // }

            //check minimum against leg 1 arrival
            if (Math.abs(arrivalTime.diff(minimumDate, 'hours')) > 55) {
                return `${type} Arrival Date/Time has to be within 55 hours of the ${minimumDateType} Donor Surgery/Pickup Date/Time`;
            }
        }

        //Arrival must be valid for Leg 2 Departure to be validated
        // if (!arrivalTimeValid && leg2DepartureTimeValid){
        //     return `${type} Arrival Date/Time must be populated for ${type} Leg 2 Departure Date/Time to be validated`;
        // }


        if (leg2DepartureTimeValid && !disableLeg2) {
            //TimeZone must be selected
            if (values.leg2DepartureTimeTz == '') {
                return `${type} Leg 2 Departure Date/Time TimeZone must be selected`;
            }

            // //Leg 2 Dep must be after Arrival and between Donor Surgery and Kidney Arrival
            // if (leg2DepartureTime.isBefore(arrivalTime) || !leg2DepartureTime.isBetween(surgeryPickUpTime, kidneyArrivalTime)){
            //     return `${type} Leg 2 Departure Date/Time must be after ${type} Arrival Date/Time, as well as between ${type} Donor Surgery/Pickup Date/Time and ${type} Kidney Arrival Date/Time. Please be cognizant of TimeZone`;
            // }

            //check minimum against leg 2 Departure
            if (Math.abs(leg2DepartureTime.diff(minimumDate, 'hours')) > 55) {
                return `${type} Leg 2 Departure Date/Time has to be within 55 hours of the ${minimumDateType} Donor Surgery/Pickup Date/Time`;
            }
        }

        //Leg 2 Dep must be valid for Leg 2 Arrival to be validated
        // if (!leg2DepartureTimeValid && leg2ArrivalTimeValid && !disableLeg2){
        //     return `${type} Leg 2 Departure Date/Time must be populated for ${type} Leg 2 Arrival/Time to be validated`;
        // }

        if (leg2ArrivalTimeValid && !disableLeg2) {
            //TimeZone must be selected
            if (values.leg2ArrivalTimeTz == '') {
                return `${type} Leg 2 Arrival Date/Time TimeZone must be selected`;
            }

            // //Leg 2 Arrival must be after Leg 2 Dep and between Donor Surgery and Kidney Arrival
            // if (leg2ArrivalTime.isBefore(leg2DepartureTime) || !leg2ArrivalTime.isBetween(surgeryPickUpTime, kidneyArrivalTime)){
            //     return `${type} Leg 2 Arrival Date/Time must be after ${type} Leg 2 Departure Date/Time, as well as between ${type} Donor Surgery/Pickup Date/Time and ${type} Kidney Arrival Date/Time`;
            // }

            //check minimum against leg 2 arrival
            if (Math.abs(leg2ArrivalTime.diff(minimumDate, 'hours')) > 55) {
                return `${type} Leg 2 Arrival Date/Time has to be within 55 hours of the ${minimumDateType} Donor Surgery/Pickup Date/Time`;
            }
        }

        //If actual, validate tendered and recovered
        if (type == 'Actual') {
            if (tenderedTimeValid) {
                //TimeZone must be selected
                if (values.tenderedTimeTz == '') {
                    return `${type} Tendered to Airline Date/Time TimeZone must be selected`;
                }

                // //Tendered must be between Departure and Arrival
                // if (!tenderedTime.isBetween(surgeryPickUpTime, kidneyArrivalTime)) {
                //     return `${type} Tendered to Airline Date/Time must be between ${type} Donor Surgery/Pickup Date/Time and ${type} Kidney Arrival Date/Time`;
                // }

                //check minimum against tendered to airline date
                if (Math.abs(tenderedTime.diff(minimumDate, 'hours')) > 55) {
                    return `${type} Tendered to Airline Date/Time has to be within 55 hours of the ${minimumDateType} Donor Surgery/Pickup Date/Time`;
                }
            }

            if (recoveredTimeValid) {
                //TimeZone must be selected
                if (values.recoveredTimeTz == '') {
                    return `${type} Recovered from Airline Date/Time TimeZone must be selected`;
                }

                // //Recovered Time
                // if (!recoveredTime.isBetween(surgeryPickUpTime, kidneyArrivalTime)) {
                //     return `${type} Recovered from Airline Date/Time must be between ${type} Donor Surgery/Pickup Date/Time and ${type} Kidney Arrival Date/Time`;
                // }

                //check minimum against Recovered to airline date
                if (Math.abs(recoveredTime.diff(minimumDate, 'hours')) > 55) {
                    return `${type} Recovered from Airline Date/Time has to be within 55 hours of the ${minimumDateType} Donor Surgery/Pickup Date/Time`;
                }
            }
        }

        return '';
    }

    handleTimeZone = (timeZone: string) => {
        if (timeZone == 'EST' || timeZone == '') {
            return 'America/New_York';
        }

        if (timeZone == 'CST') {
            return 'America/Chicago';
        }

        if (timeZone == 'MST') {
            return 'America/Denver';
        }

        if (timeZone == 'MST-Az') {
            return 'America/Phoenix'
        }

        if (timeZone == 'PST') {
            return 'America/Los_Angeles';
        }

        if (timeZone == 'HST') {
            return 'Pacific/Honolulu';
        }
    }

    handleDateConversion = (date: string, timeZone: string): [boolean, moment.Moment] => {
        let dateConverted = moment.utc();
        let validDate = false;

        if (date != '' || date != undefined || date != null) {
            if (moment(date, 'MM/DD/YY HH:mm', true).isValid()) {
                validDate = true;
                let newDateConverted = moment.tz(date, 'MM/DD/YY HH:mm', this.handleTimeZone(timeZone));
                dateConverted = newDateConverted.utc();
            }
        }

        return [validDate, dateConverted]
    }

    returnPickUpMinimumDate = (): [moment.Moment, string] => {
        let { kidneyPickupDateTime, kidneyPickupDateTimeTz, kidneyPickupDateTimeBackup } = this.state.data;

        let [primaryValid, primaryDate] = this.handleDateConversion(kidneyPickupDateTime, kidneyPickupDateTimeTz);
        let [backupValid, backUpDate] = this.handleDateConversion(kidneyPickupDateTimeBackup, kidneyPickupDateTimeTz);

        if (primaryValid && backupValid) {

            if (primaryDate.isAfter(backUpDate)) {
                return [backUpDate, 'Back Up'];
            }

            return [primaryDate, 'Primary'];

        } else if (primaryValid && !backupValid) {

            return [primaryDate, 'Primary'];

        } else if (!primaryValid && backupValid) {

            return [backUpDate, 'Back Up'];

        }

        return [moment.utc(), ''];
    }

    render = () => {
        const position = this.props.data != null ? this.props.data.position : 0;
        const swapNumber = this.props.data != null ? `${this.props.data.chainId}/${this.props.data.clusterNumber}/${position}` : "";
        const donorCenter = this.props.data != null ? this.props.data.donorCenter : "";
        const donorAlias = this.props.data != null ? this.props.data.donorAlias : "";
        const rdnCenterId = this.props.data != null ? this.props.data.rdnCenterId : 0;
        const rdnCenterAlias = this.props.data != null ? this.props.data.rdnCenterAlias : "";
        const recipientCenter = this.props.data != null ? this.props.data.recipientCenter : "";
        const recipientAlias = this.props.data != null ? this.props.data.recipientAlias : "";
        const donorCenterId = rdnCenterId > 0 ? rdnCenterId : this.props.data != null ? this.props.data.donorCenterId : 0;
        const recipientCenterId = this.props.data != null ? this.props.data.recipientCenterId : 0;

        const earliestPickup = this.props.data != null ? this.props.data.earliestPickupTime : "";
        const latestDropoff = this.props.data != null ? this.props.data.latestDropoffTime : "";

        const donorSurgeons = this.props.data != null ? this.props.data.donorSurgeons : [];
        const recipientSurgeons = this.props.data != null ? this.props.data.recipientSurgeons : [];

        const {
            kidneyPickupDateTimeValid, kidneyPickupDateTimeBackupValid, kidneyPickupDateTimeActualValid,
            kidneyArrivalDateTimeValid, kidneyArrivalDateTimeBackupValid, kidneyArrivalDateTimeActualValid,
            flightDepartureTimeValid, flightDepartureTimeBackupValid, flightDepartureTimeActualValid,
            flightArrivalTimeValid, flightArrivalTimeBackupValid, flightArrivalTimeActualValid,
            leg2DepartureTimeValid, leg2DepartureTimeBackupValid, leg2DepartureTimeActualValid,
            leg2ArrivalTimeValid, leg2ArrivalTimeBackupValid, leg2ArrivalTimeActualValid,
            tenderedToAirlineActualValid, recoveredFromAirlineActualValid } = this.state.data;

        let valid =
            kidneyPickupDateTimeValid && kidneyPickupDateTimeBackupValid && kidneyPickupDateTimeActualValid
            && kidneyArrivalDateTimeValid && kidneyArrivalDateTimeBackupValid && kidneyArrivalDateTimeActualValid
            && flightDepartureTimeValid && flightDepartureTimeBackupValid && flightDepartureTimeActualValid
            && flightArrivalTimeValid && flightArrivalTimeBackupValid && flightArrivalTimeActualValid
            && leg2DepartureTimeValid && leg2DepartureTimeBackupValid && leg2DepartureTimeActualValid
            && leg2ArrivalTimeValid && leg2ArrivalTimeBackupValid && leg2ArrivalTimeActualValid
            && tenderedToAirlineActualValid && recoveredFromAirlineActualValid;

        const forCourier = this.props.forCourier != null ? this.props.forCourier : false;
        const showDateError = this.state.showDateError;
        const messages = this.state.messages;

        return (
            <>
                <Modal bsSize="lg" backdrop="static" show={this.props.show} onHide={this.handleCancel} style={{ zIndex: 99999 }}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Logistic Plans - <span style={{ fontWeight: 700, color: "#133366" }}>Swap {swapNumber}</span>
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {showDateError &&
                            <Alert bsStyle='danger' style={{ textAlign: 'center', marginBottom: 0 }}>
                                <div>
                                    <ul>
                                        {messages.map(x => {
                                            return <li style={{ marginTop: '2px' }}>{x}</li>
                                        })}
                                    </ul>
                                </div>
                            </Alert>
                        }

                        <LogisticsEditContent
                            data={this.state.data}
                            swapNumber={swapNumber}
                            donorCenter={donorCenter}
                            donorAlias={donorAlias}
                            rdnCenterId={rdnCenterId}
                            donorCenterId={donorCenterId}
                            recipientCenterId={recipientCenterId}
                            rdnCenterAlias={rdnCenterAlias}
                            recipientCenter={recipientCenter}
                            recipientAlias={recipientAlias}
                            earliestPickup={earliestPickup}
                            latestDropoff={latestDropoff}
                            logisticsProviders={this.props.logisticsProviders}
                            transportTypes={this.props.transportTypes}
                            donorSurgeons={donorSurgeons}
                            recipientSurgeons={recipientSurgeons}
                            valid={valid}
                            forCourier={forCourier}
                            distanceBetweenCentersList={this.props.distanceBetweenCentersList}
                            onDateTimeFieldChange={this.handleDateTimeFieldChange}
                            onFieldChange={this.handleFieldChange}
                            onDropdownChange={this.handleDropdownChange}
                            onActiveLogisticsPlanChange={this.handleActiveLogisticsPlanChange}
                            onCopyPrimary={this.handleCopyPrimary}
                            onCopyBackup={this.handleCopyBackup}
                        />

                        <Form horizontal>
                            <FormGroup>
                                <Col xs={12} lg={1} componentClass={ControlLabel}>Notes</Col>
                                <Col xs={12} lg={11}>
                                    <FormControl
                                        componentClass="textarea"
                                        style={{ resize: 'none' }}
                                        value={this.state.data.notes}
                                        onChange={this.handleNotesChange}
                                    />
                                </Col>
                            </FormGroup>
                        </Form>
                    </Modal.Body>

                    <Modal.Footer>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                {!forCourier && (
                                    <>
                                        <Button value="logistics_plan" onClick={this.handleDownload}>Logistics Plan</Button>
                                        <Button value="logistics_call_checklist" onClick={this.handleDownload}>Call Checklists</Button>
                                    </>
                                )}

                                <Button value="logistics_address_list" onClick={this.handleAddressDownload}>Addresses</Button>
                                <Button value="logistics_contact_list" onClick={this.handleDownload}>Contacts</Button>

                                {!forCourier && (
                                    <Button className="btn-swap-logistics-plan" onClick={this.handleSwitchToLogisticsPlan}>Plan Options</Button>
                                )}
                            </div>
                            <div style={{ display: "flex" }}>
                                <Button bsStyle="primary" onClick={this.handleSave} disabled={this.state.dirty == false}>Save Logistics</Button>
                                <Button onClick={this.handleCancel}>Cancel</Button>
                            </div>
                        </div>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

export default Logistics;
